import { useMemo, useState } from 'react';
import { Body1Strong, Caption1, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';

import { TChangeTemplate } from '../../models';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxHeight: '420px',
    overflowY: 'auto',
    // Keep the scrollbar off the card borders so focus rings stay visible.
    paddingRight: tokens.spacingHorizontalXXS,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  featured: { border: `1px solid ${tokens.colorBrandStroke2}` },
  selected: { border: `2px solid ${tokens.colorBrandStroke1}`, backgroundColor: tokens.colorBrandBackground2 },
  muted: { color: tokens.colorNeutralForeground3 },
});

type TProps = {
  templates: TChangeTemplate[];
  selectedId?: string;
  onSelect: (id: string) => void;
};

export const TemplateGallery = ({ templates, selectedId, onSelect }: TProps) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');

  // Featured templates lead the list; the remainder keep their configured order.
  const ordered = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase();
    const matched = normalized
      ? templates.filter((template) =>
          `${template.name} ${template.description} ${template.applicability}`.toLocaleLowerCase().includes(normalized)
        )
      : templates;
    return [...matched].sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)));
  }, [templates, search]);

  return (
    <div className={classes.root}>
      <Input
        contentBefore={<SearchRegular />}
        placeholder="Search templates"
        value={search}
        onChange={(_, data) => setSearch(data.value)}
      />
      <div className={classes.list}>
        {ordered.map((template) => (
          <button
            key={template.id}
            type="button"
            className={mergeClasses(
              classes.card,
              template.featured && classes.featured,
              selectedId === template.id && classes.selected
            )}
            onClick={() => onSelect(template.id)}
            aria-pressed={selectedId === template.id}
          >
            <Body1Strong>{template.name}</Body1Strong>
            <Caption1>{template.description}</Caption1>
            <Caption1 className={classes.muted}>{template.applicability}</Caption1>
          </button>
        ))}
        {ordered.length === 0 ? <Caption1 className={classes.muted}>No templates match your search.</Caption1> : null}
      </div>
    </div>
  );
};
